// LessonDesignView.jsx
import React, { useMemo, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { edgeFunctionService } from '../../services/edgeFunctionService'
import Layout from '../Layout/Layout'
import QuestionWizard from '../Wizard/QuestionWizard'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiArrowLeft,
  FiPlay,
  FiRefreshCw,
  FiCheck,
  FiDownload,
  FiCopy,
  FiEdit2,
  FiX,
  FiShuffle,
  FiZap,
  FiRotateCcw,
} = FiIcons

// Helper: transform lesson plan markdown into styled HTML,
// including subtle, right-aligned HITS blocks and improved spacing.
const transformLessonPlanMarkdown = (markdown) => {
  if (!markdown) return ''

  let text = markdown.replace(
    /HITS:\s*\n((?:- .*(?:\n|$))+)/g,
    (_match, hitsLines) => {
      const itemsHtml = hitsLines
        .trim()
        .split('\n')
        .map((line) => line.replace(/^- /, '').trim())
        .filter(Boolean)
        .map((item) => `<li class="mb-0">${item}</li>`)
        .join('')

      return `
<div class="mt-4 text-[11px] text-gray-400 text-right">
  <div class="font-semibold tracking-wide uppercase mb-1">HITS</div>
  <ul class="inline-block text-right list-none space-y-1">
    ${itemsHtml}
  </ul>
</div>
`
    },
  )

  let html = text
    .replace(
      /^# (.*$)/gim,
      '<h1 class="text-2xl font-bold text-gray-900 mb-4 mt-2">$1</h1>',
    )
    .replace(
      /^## (.*$)/gim,
      '<h2 class="text-xl font-semibold text-gray-900 mb-3 mt-6">$1</h2>',
    )
    .replace(
      /^### (.*$)/gim,
      '<h3 class="text-lg font-semibold text-gray-900 mb-2 mt-4">$1</h3>',
    )
    .replace(
      /\*\*(.*?)\*\*/gim,
      '<strong class="font-semibold text-gray-900">$1</strong>',
    )
    .replace(/^- (.*$)/gim, '<li class="ml-5 mb-1 leading-snug">$1</li>')
    .replace(/\n\n/g, '</p><p class="mb-4">')
    .replace(/^(?!<(?:h|l|s|d|u|p))/gm, '<p class="mb-3 leading-relaxed">')

  return html
}

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'discussion', label: 'Discussion' },
  { value: 'pair_work', label: 'Pair work' },
  { value: 'small_group_work', label: 'Small group work' },
  { value: 'individual_work', label: 'Individual work' },
  { value: 'practical_work', label: 'Practical work' },
  { value: 'teacher_instruction', label: 'Teacher instruction' },
  { value: 'other', label: 'Other' },
]

const prettyActivityType = (t) => {
  const found = ACTIVITY_TYPE_OPTIONS.find((x) => x.value === t)
  return found?.label ?? String(t || 'Other')
}

const LessonDesignView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { userProfile } = useAuth()

  const [lessonDesign, setLessonDesign] = useState(null)
  const [stimulus, setStimulus] = useState(null)
  const [themes, setThemes] = useState([])
  const [lessonPlans, setLessonPlans] = useState([])
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [generatingResources, setGeneratingResources] = useState(false)
  const [editingQuestions, setEditingQuestions] = useState(false)

  // Activities
  const [activities, setActivities] = useState([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)

  // Swap drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerSlotIndex, setDrawerSlotIndex] = useState(null)
  const [drawerMode, setDrawerMode] = useState('suggest') // suggest | swap
  const [teacherGuidance, setTeacherGuidance] = useState('')
  const [requestedActivityType, setRequestedActivityType] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [selectedSuggestionIdx, setSelectedSuggestionIdx] = useState(null)
  const [swapLoading, setSwapLoading] = useState(false)
  const [suggestLoading, setSuggestLoading] = useState(false)
  const [drawerError, setDrawerError] = useState('')

  // Revision history for currently opened slot
  const [slotRevisions, setSlotRevisions] = useState([])
  const [revisionsLoading, setRevisionsLoading] = useState(false)

  useEffect(() => {
    if (userProfile && id) {
      fetchLessonDesign()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, id])

  const fetchLessonDesign = async () => {
    try {
      const { data: design, error: designError } = await supabase
        .schema('classroom')
        .from('lesson_designs')
        .select('*')
        .eq('id', id)
        .eq('user_id', userProfile.id)
        .single()

      if (designError) throw designError
      setLessonDesign(design)

      const { data: stimulusData, error: stimulusError } = await supabase
        .schema('classroom')
        .from('stimuli')
        .select('*')
        .eq('id', design.stimulus_id)
        .single()

      if (stimulusError) throw stimulusError
      setStimulus(stimulusData)

      const { data: themesData, error: themesError } = await supabase
        .schema('classroom')
        .from('lesson_design_themes')
        .select(`stimulus_themes (*)`)
        .eq('lesson_design_id', design.id)

      if (themesError) throw themesError
      setThemes(themesData.map((t) => t.stimulus_themes))

      if (design.status !== 'collecting_input') {
        const { data: plansData, error: plansError } = await supabase
          .schema('classroom')
          .from('lesson_plans')
          .select('*')
          .eq('lesson_design_id', design.id)
          .order('version', { ascending: false })

        if (plansError) throw plansError
        setLessonPlans(plansData || [])
      }

      if (design.status === 'finalised') {
        const { data: resourcesData, error: resourcesError } = await supabase
          .schema('classroom')
          .from('lesson_resources')
          .select('*')
          .eq('lesson_design_id', design.id)
          .order('created_at', { ascending: true })

        if (resourcesError) throw resourcesError
        setResources(resourcesData || [])
      }
    } catch (error) {
      console.error('Error fetching lesson design:', error)
      navigate('/dashboard')
    } finally {
      setLoading(false)
    }
  }

  const currentPlan =
    lessonPlans.find((p) => p.is_current) || lessonPlans[0] || null

  const hasGeneratedPlan = !!currentPlan
  const isCollectingInput = lessonDesign?.status === 'collecting_input'

  const lessonPlanHtml = currentPlan
    ? transformLessonPlanMarkdown(currentPlan.plan_markdown)
    : ''

  const fetchActivitiesForPlan = async (lessonPlanId) => {
    if (!lessonPlanId) {
      setActivities([])
      return
    }

    setActivitiesLoading(true)
    try {
      const { data, error } = await supabase
        .schema('classroom')
        .from('lesson_plan_activities')
        .select('*')
        .eq('lesson_plan_id', lessonPlanId)
        .eq('is_current', true)
        .order('slot_index', { ascending: true })

      if (error) throw error
      setActivities(data || [])
    } catch (e) {
      console.error('Failed to fetch lesson_plan_activities:', e)
      setActivities([])
    } finally {
      setActivitiesLoading(false)
    }
  }

  useEffect(() => {
    if (currentPlan?.id) {
      fetchActivitiesForPlan(currentPlan.id)
    } else {
      setActivities([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan?.id])

  const openDrawerForSlot = async (slotIndex) => {
    setDrawerOpen(true)
    setDrawerSlotIndex(slotIndex)
    setDrawerMode('suggest')
    setTeacherGuidance('')
    setRequestedActivityType('')
    setSuggestions([])
    setSelectedSuggestionIdx(null)
    setDrawerError('')
    await fetchRevisionsForSlot(currentPlan?.id, slotIndex)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setDrawerSlotIndex(null)
    setSuggestions([])
    setSelectedSuggestionIdx(null)
    setDrawerError('')
    setSlotRevisions([])
  }

  const drawerActivity = useMemo(() => {
    if (drawerSlotIndex == null) return null
    return activities.find((a) => a.slot_index === drawerSlotIndex) || null
  }, [activities, drawerSlotIndex])

  const fetchRevisionsForSlot = async (lessonPlanId, slotIndex) => {
    if (!lessonPlanId || slotIndex == null) return
    setRevisionsLoading(true)
    try {
      const { data, error } = await supabase
        .schema('classroom')
        .from('lesson_plan_activities')
        .select('*')
        .eq('lesson_plan_id', lessonPlanId)
        .eq('slot_index', slotIndex)
        .order('revision', { ascending: false })

      if (error) throw error
      setSlotRevisions(data || [])
    } catch (e) {
      console.error('Failed to load slot revisions:', e)
      setSlotRevisions([])
    } finally {
      setRevisionsLoading(false)
    }
  }

  const getSuggestions = async () => {
    if (!currentPlan?.id || drawerSlotIndex == null) return
    setSuggestLoading(true)
    setDrawerError('')
    setSuggestions([])
    setSelectedSuggestionIdx(null)

    try {
      const payload = {
        lesson_plan_id: currentPlan.id,
        slot_index: drawerSlotIndex,
        mode: 'suggest',
        teacher_guidance: teacherGuidance?.trim() ? teacherGuidance.trim() : null,
        requested_activity_type: requestedActivityType?.trim()
          ? requestedActivityType.trim()
          : null,
        selected_suggestion: null,
      }

      const { data, error } = await supabase.functions.invoke(
        'swap_lesson_activity',
        { body: payload },
      )

      if (error) {
        console.error('swap_lesson_activity suggest invoke error:', error)
        throw new Error(error.message || 'Failed to get suggestions')
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to get suggestions')
      }

      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : [])
    } catch (e) {
      console.error(e)
      setDrawerError(e?.message || 'Failed to get suggestions')
    } finally {
      setSuggestLoading(false)
    }
  }

  const commitSwap = async () => {
    if (!currentPlan?.id || drawerSlotIndex == null) return
    setSwapLoading(true)
    setDrawerError('')

    try {
      const selected =
        selectedSuggestionIdx == null
          ? null
          : suggestions[selectedSuggestionIdx] || null

      const payload = {
        lesson_plan_id: currentPlan.id,
        slot_index: drawerSlotIndex,
        mode: 'swap',
        teacher_guidance: teacherGuidance?.trim() ? teacherGuidance.trim() : null,
        requested_activity_type: requestedActivityType?.trim()
          ? requestedActivityType.trim()
          : null,
        selected_suggestion: selected,
      }

      const { data, error } = await supabase.functions.invoke(
        'swap_lesson_activity',
        { body: payload },
      )

      if (error) {
        console.error('swap_lesson_activity swap invoke error:', error)
        throw new Error(error.message || 'Failed to swap activity')
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to swap activity')
      }

      // Refresh activities + revisions so UI reflects new current revision
      await fetchActivitiesForPlan(currentPlan.id)
      await fetchRevisionsForSlot(currentPlan.id, drawerSlotIndex)

      // reset suggestion state (keep drawer open so they can read)
      setSuggestions([])
      setSelectedSuggestionIdx(null)
      setDrawerMode('suggest')
      setTeacherGuidance('')
      setRequestedActivityType('')
    } catch (e) {
      console.error(e)
      setDrawerError(e?.message || 'Failed to swap activity')
    } finally {
      setSwapLoading(false)
    }
  }

  /**
   * Revert to a specific revision (DB-only).
   * This DOES NOT call OpenAI, so does not consume tokens.
   *
   * Implementation: mark current false, insert a new revision copying the chosen revision.
   * This preserves the revision history and keeps "current" semantics consistent.
   */
  const revertToRevision = async (targetActivityRow) => {
    if (!currentPlan?.id || drawerSlotIndex == null || !targetActivityRow) return
    setSwapLoading(true)
    setDrawerError('')

    try {
      const current = activities.find((a) => a.slot_index === drawerSlotIndex)
      if (!current) throw new Error('Current activity not found')

      const nextRevision = Number(current.revision || 1) + 1

      // 1) unset current
      const { error: unsetErr } = await supabase
        .schema('classroom')
        .from('lesson_plan_activities')
        .update({ is_current: false })
        .eq('lesson_plan_id', currentPlan.id)
        .eq('slot_index', drawerSlotIndex)
        .eq('is_current', true)

      if (unsetErr) throw unsetErr

      // 2) insert a new revision copying target content
      const { error: insErr } = await supabase
        .schema('classroom')
        .from('lesson_plan_activities')
        .insert({
          lesson_plan_id: currentPlan.id,
          slot_index: drawerSlotIndex,
          revision: nextRevision,
          is_current: true,
          activity_type: targetActivityRow.activity_type,
          title: targetActivityRow.title,
          activity_markdown: targetActivityRow.activity_markdown,
          estimated_minutes: targetActivityRow.estimated_minutes ?? null,
        })

      if (insErr) throw insErr

      await fetchActivitiesForPlan(currentPlan.id)
      await fetchRevisionsForSlot(currentPlan.id, drawerSlotIndex)
    } catch (e) {
      console.error(e)
      setDrawerError(e?.message || 'Failed to revert activity')
    } finally {
      setSwapLoading(false)
    }
  }

  const handleGeneratePlan = async () => {
    setGenerating(true)
    try {
      const result = await edgeFunctionService.generateLessonPlan(id)
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate lesson plan')
      }

      setLessonDesign((prev) => ({
        ...prev,
        status: 'plan_generated',
      }))
      setLessonPlans((prev) => [result.lessonPlan, ...prev])

      // After a new plan is created, activities will be seeded by the edge function.
      // We'll fetch once currentPlan updates via state.
    } catch (error) {
      console.error('Error generating lesson plan:', error)
      alert('Failed to generate lesson plan. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const handleAcceptPlan = async () => {
    try {
      const { error } = await supabase
        .schema('classroom')
        .from('lesson_designs')
        .update({ status: 'finalised' })
        .eq('id', lessonDesign.id)

      if (error) throw error
      setLessonDesign((prev) => ({ ...prev, status: 'finalised' }))
    } catch (error) {
      console.error('Error accepting plan:', error)
      alert('Failed to accept plan. Please try again.')
    }
  }

  const handleGenerateResources = async () => {
    setGeneratingResources(true)
    try {
      const result = await edgeFunctionService.generateLessonResources(id)
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate lesson resources')
      }
      setResources(result.resources || [])
    } catch (error) {
      console.error('Error generating lesson resources:', error)
      alert('Failed to generate lesson resources. Please try again.')
    } finally {
      setGeneratingResources(false)
    }
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      alert('Copied to clipboard!')
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      </Layout>
    )
  }

  if (!lessonDesign) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">Lesson design not found</div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:text-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </Layout>
    )
  }

  // Show question wizard if still collecting input OR user clicked "Edit answers"
  if (isCollectingInput || editingQuestions) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            {isCollectingInput ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                <span>Back to Dashboard</span>
              </button>
            ) : (
              <button
                onClick={() => setEditingQuestions(false)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                <span>Back to summary</span>
              </button>
            )}
          </div>
          <QuestionWizard
            lessonDesign={lessonDesign}
            onComplete={(updatedDesign) => {
              setLessonDesign((prev) => {
                if (!prev) return updatedDesign
                const merged = { ...prev, ...updatedDesign }
                if (!isCollectingInput) merged.status = prev.status
                return merged
              })
              setEditingQuestions(false)
            }}
          />
        </div>
      </Layout>
    )
  }

  const statusLabel =
    lessonDesign.status === 'finalised'
      ? 'Finalised'
      : hasGeneratedPlan
      ? 'Plan generated'
      : 'Ready to generate'

  const statusClasses =
    lessonDesign.status === 'finalised'
      ? 'bg-green-100 text-green-800'
      : hasGeneratedPlan
      ? 'bg-blue-100 text-blue-800'
      : 'bg-yellow-100 text-yellow-800'

  const usingActivities = activities && activities.length > 0

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${statusClasses}`}
            >
              {statusLabel}
            </span>
          </div>
        </div>

        {/* Lesson Info / Summary of choices */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {lessonDesign.subject} - {lessonDesign.year_level}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {lessonDesign.is_series
                  ? `Series of ${lessonDesign.lesson_count} lessons • ${lessonDesign.lesson_duration_minutes} mins each`
                  : `${lessonDesign.lesson_duration_minutes} minute lesson`}
              </p>
            </div>
            {lessonDesign.status !== 'finalised' && (
              <button
                type="button"
                onClick={() => setEditingQuestions(true)}
                className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                <span>Edit answers</span>
              </button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Stimulus</h3>
              <p className="text-gray-700 font-medium">
                {stimulus?.title || 'Untitled'}
              </p>
              {stimulus?.description && (
                <p className="text-gray-600 text-sm mt-1">
                  {stimulus.description}
                </p>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Learning themes
              </h3>
              {themes.length === 0 ? (
                <p className="text-sm text-gray-500">No themes selected.</p>
              ) : (
                <div className="space-y-1">
                  {themes.map((theme, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium text-gray-700">
                        {theme.label}
                      </span>
                      {theme.description && (
                        <span className="text-gray-500 ml-2">
                          – {theme.description}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3 text-sm">
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">
                Duration
              </div>
              <div className="mt-1 text-gray-800">
                {lessonDesign.lesson_duration_minutes} minutes
              </div>
            </div>
            {lessonDesign.is_series && (
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">
                  Series
                </div>
                <div className="mt-1 text-gray-800">
                  {lessonDesign.lesson_count} lessons
                </div>
              </div>
            )}
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">
                Activities
              </div>
              <div className="mt-1 text-gray-800">
                {lessonDesign.activity_preferences?.length
                  ? lessonDesign.activity_preferences.join(', ')
                  : 'Not specified'}
              </div>
            </div>
          </div>
        </div>

        {/* Generate Plan Section */}
        {lessonPlans.length === 0 &&
          lessonDesign.status !== 'collecting_input' && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Ready to Generate Your Lesson Plan
              </h2>
              <p className="text-gray-600 mb-6">
                All questions have been answered. Click below to generate your
                personalized lesson plan.
              </p>
              <button
                onClick={handleGeneratePlan}
                disabled={generating}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    <span>Generating Lesson Plan...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiPlay} className="w-4 h-4" />
                    <span>Generate Lesson Plan</span>
                  </>
                )}
              </button>
            </div>
          )}

        {/* Lesson Plan Display */}
        {currentPlan && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Generated Lesson Plan
                </h2>
                <p className="text-gray-600">Version {currentPlan.version}</p>
                {usingActivities ? (
                  <p className="text-xs text-gray-500 mt-1">
                    Viewing editable activities (swaps update instantly)
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Viewing legacy full markdown (no activities found for this plan)
                  </p>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => copyToClipboard(currentPlan.plan_markdown)}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  title="Copy original full plan markdown"
                >
                  <SafeIcon icon={FiCopy} className="w-4 h-4" />
                  <span>Copy</span>
                </button>
                {lessonDesign.status === 'plan_generated' && (
                  <>
                    <button
                      onClick={handleGeneratePlan}
                      disabled={generating}
                      className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                      <span>{generating ? 'Generating...' : 'Try Again'}</span>
                    </button>
                    <button
                      onClick={handleAcceptPlan}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span>Accept Plan</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Activities-first view */}
            {usingActivities ? (
              <>
                {activitiesLoading ? (
                  <div className="text-gray-500">Loading activities…</div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((a) => (
                      <div
                        key={`${a.lesson_plan_id}-${a.slot_index}-${a.revision}`}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                Activity {a.slot_index + 1}
                              </span>
                              <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                                {prettyActivityType(a.activity_type)}
                              </span>
                              {a.estimated_minutes != null && (
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-600">
                                  {a.estimated_minutes} min
                                </span>
                              )}
                              <span className="text-[11px] text-gray-400">
                                rev {a.revision}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mt-2">
                              {a.title || 'Untitled activity'}
                            </h3>
                          </div>

                          <button
                            type="button"
                            onClick={() => openDrawerForSlot(a.slot_index)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                            title="Swap or revise this activity"
                          >
                            <SafeIcon icon={FiShuffle} className="w-4 h-4" />
                            Swap
                          </button>
                        </div>

                        <div className="mt-3 prose max-w-none">
                          <div
                            className="whitespace-pre-wrap text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: transformLessonPlanMarkdown(
                                a.activity_markdown,
                              ),
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Optional: original markdown collapsible */}
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
                    View original full plan markdown (snapshot)
                  </summary>
                  <div className="mt-3 prose max-w-none">
                    <div
                      className="whitespace-pre-wrap text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: lessonPlanHtml }}
                    />
                  </div>
                </details>
              </>
            ) : (
              // Legacy fallback
              <div className="prose max-w-none">
                <div
                  className="whitespace-pre-wrap text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: lessonPlanHtml }}
                />
              </div>
            )}
          </div>
        )}

        {/* Resources Section */}
        {lessonDesign.status === 'finalised' && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Lesson Resources
              </h2>
              {resources.length === 0 && (
                <button
                  onClick={handleGenerateResources}
                  disabled={generatingResources}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingResources ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      <span>Generating Resources...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>Generate Resources</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {resources.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No resources generated yet. Click the button above to create
                worksheets, slideshows, and homework tasks.
              </div>
            ) : (
              <div className="space-y-6">
                {['worksheet', 'slideshow', 'homework', 'other'].map((type) => {
                  const typeResources = resources.filter((r) => r.type === type)
                  if (typeResources.length === 0) return null
                  return (
                    <div key={type}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 capitalize">
                        {type === 'slideshow' ? 'Slideshow' : `${type}s`}
                      </h3>
                      <div className="space-y-4">
                        {typeResources.map((resource) => (
                          <div
                            key={resource.id}
                            className="border border-gray-200 rounded-lg p-4"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="font-medium text-gray-900">
                                {resource.title}
                              </h4>
                              <button
                                onClick={() =>
                                  copyToClipboard(resource.content_markdown)
                                }
                                className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                              >
                                <SafeIcon icon={FiCopy} className="w-3 h-3" />
                                <span>Copy</span>
                              </button>
                            </div>
                            <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                              {resource.content_markdown}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* -------------------- Side Drawer (Swap Activity) -------------------- */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50">
          {/* overlay */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeDrawer}
            role="presentation"
          />

          {/* panel */}
          <div className="absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl border-l border-gray-200 flex flex-col">
            <div className="p-5 border-b border-gray-200 flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                  Swap activity
                </div>
                <div className="text-lg font-semibold text-gray-900 mt-1">
                  Activity {drawerSlotIndex != null ? drawerSlotIndex + 1 : ''}
                </div>
                {drawerActivity?.title && (
                  <div className="text-sm text-gray-600 mt-1">
                    {drawerActivity.title}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 rounded hover:bg-gray-50 text-gray-600"
                title="Close"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-6">
              {/* Current context */}
              <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">
                    Current activity (editable)
                  </div>
                  {drawerActivity && (
                    <div className="text-xs text-gray-500">
                      {prettyActivityType(drawerActivity.activity_type)} •{' '}
                      {drawerActivity.estimated_minutes ?? '—'} min • rev{' '}
                      {drawerActivity.revision}
                    </div>
                  )}
                </div>
                {drawerActivity?.activity_markdown ? (
                  <div className="mt-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {drawerActivity.activity_markdown}
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-gray-500">
                    No activity found for this slot.
                  </div>
                )}
              </div>

              {/* Teacher guidance */}
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  What should change?
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Optional. Use free text and/or choose a suggested activity.
                </p>
                <textarea
                  value={teacherGuidance}
                  onChange={(e) => setTeacherGuidance(e.target.value)}
                  rows={4}
                  className="mt-2 w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g., Make this more hands-on. Reduce teacher talk. Include more checking for understanding. Tie it more explicitly to the stimulus..."
                />
              </div>

              {/* Type constraint */}
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Optional activity type constraint
                </div>
                <select
                  value={requestedActivityType}
                  onChange={(e) => setRequestedActivityType(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">No constraint (AI chooses)</option>
                  {ACTIVITY_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Suggestions */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Suggested replacements
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Click “Get suggestions” then choose one (optional).
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={getSuggestions}
                    disabled={suggestLoading || !currentPlan?.id}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {suggestLoading ? (
                      <>
                        <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                        Loading…
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiZap} className="w-4 h-4" />
                        Get suggestions
                      </>
                    )}
                  </button>
                </div>

                {suggestions.length === 0 ? (
                  <div className="mt-3 text-sm text-gray-500">
                    No suggestions loaded yet.
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {suggestions.map((s, idx) => {
                      const active = selectedSuggestionIdx === idx
                      return (
                        <button
                          key={`${s.title}-${idx}`}
                          type="button"
                          onClick={() =>
                            setSelectedSuggestionIdx(active ? null : idx)
                          }
                          className={[
                            'w-full text-left rounded-lg border p-3 transition-colors',
                            active
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-200 hover:bg-gray-50',
                          ].join(' ')}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-semibold text-gray-900 text-sm">
                              {s.title}
                            </div>
                            <div className="text-xs text-gray-500 shrink-0">
                              {prettyActivityType(s.activity_type)} •{' '}
                              {s.estimated_minutes ?? '—'} min
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 mt-2">
                            {s.description}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Errors */}
              {drawerError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {drawerError}
                </div>
              )}

              {/* Revision history + revert */}
              <div className="border-t border-gray-200 pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Revision history
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Revert without using AI tokens.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      fetchRevisionsForSlot(currentPlan?.id, drawerSlotIndex)
                    }
                    className="text-xs text-gray-600 hover:text-gray-900"
                    disabled={revisionsLoading}
                  >
                    Refresh
                  </button>
                </div>

                {revisionsLoading ? (
                  <div className="mt-3 text-sm text-gray-500">
                    Loading revisions…
                  </div>
                ) : slotRevisions.length === 0 ? (
                  <div className="mt-3 text-sm text-gray-500">
                    No revisions found.
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {slotRevisions.map((r) => {
                      const isCurrent = !!r.is_current
                      return (
                        <div
                          key={r.id}
                          className={[
                            'rounded-lg border p-3',
                            isCurrent
                              ? 'border-green-300 bg-green-50'
                              : 'border-gray-200 bg-white',
                          ].join(' ')}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs text-gray-500">
                                rev {r.revision}{' '}
                                {isCurrent ? '(current)' : ''}
                              </div>
                              <div className="text-sm font-semibold text-gray-900 mt-1">
                                {r.title || 'Untitled'}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {prettyActivityType(r.activity_type)} •{' '}
                                {r.estimated_minutes ?? '—'} min
                              </div>
                            </div>

                            {!isCurrent && (
                              <button
                                type="button"
                                onClick={() => revertToRevision(r)}
                                disabled={swapLoading}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                title="Revert to this revision"
                              >
                                <SafeIcon
                                  icon={FiRotateCcw}
                                  className="w-4 h-4"
                                />
                                Revert
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Drawer footer actions */}
            <div className="p-5 border-t border-gray-200 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={closeDrawer}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>

              <button
                type="button"
                onClick={commitSwap}
                disabled={swapLoading || !currentPlan?.id || drawerSlotIndex == null}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black disabled:opacity-50"
                title="Commit swap (uses AI tokens)"
              >
                {swapLoading ? (
                  <>
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Swapping…
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiShuffle} className="w-4 h-4" />
                    Swap this activity
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default LessonDesignView